import React from 'react';
import { fpBackButton, fpCard, fpHeader, fpHeaderTitle, fpStickerSmall } from '../utils/fpUi';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Users, Mail, ExternalLink, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { CURRENT_APP_VERSION } from './Home';

export default function InfoCredits() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isDarkMode } = useDarkMode();

  const handleOpenTwitter = () => {
    window.open('https://x.com/FootParadiseArt', '_blank', 'noopener,noreferrer');
  };

  const handleOpenWebsite = () => {
    window.open('http://www.footparadiseart.com/', '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{
      ...styles.container,
      ...(isDarkMode ? { backgroundImage: 'none', backgroundColor: '#000000' } : {}),
    }}>
      <div style={{
        ...styles.header,
        ...(isDarkMode ? { backgroundColor: '#000000', borderBottom: '1px solid #333', boxShadow: 'none' } : {}),
      }}>
        <button onClick={() => navigate('/')} style={{
          ...styles.backButton,
          ...(isDarkMode ? { background: '#ffffff' } : {}),
        }}>
          <ArrowLeft size={20} color={isDarkMode ? "#000" : "#fff"} />
        </button>
        <h2 style={styles.title}>{t('infoCredits')}</h2>
        <div style={styles.spacer} />
      </div>
      
      <div style={styles.content}>
        <div style={{
          ...styles.card,
          ...(isDarkMode ? { backgroundColor: '#111111', border: '1px solid #333' } : {}),
        }}>
          {/* Logo/Brand Section */}
          <div style={styles.brandSection}>
            <div style={styles.logoContainer}>
              <img src="/logo_credits.png" alt="FootParadise" style={styles.logoImage} />
            </div>
            <p style={styles.brandTagline}>{t('premiumCollection')}</p>
          </div>

          {/* About Section */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <Users size={24} color="#FF1493" />
              <h3 style={styles.sectionTitle}>{t('aboutUs')}</h3>
            </div>
            <p style={styles.sectionText}>
              {t('aboutUsText')}
            </p>
          </div>

          {/* Connect Section */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <Heart size={24} color="#FF1493" />
              <h3 style={styles.sectionTitle}>{t('connectWithUs')}</h3>
            </div>
            <p style={styles.sectionText}>
              {t('connectText')}
            </p>
            
            <button onClick={handleOpenTwitter} style={styles.socialButton}>
              <div style={styles.socialIcon}>𝕏</div>
              <div style={styles.socialInfo}>
                <span style={styles.socialName}>Twitter / X</span>
                <span style={styles.socialHandle}>@FootParadiseArt</span>
              </div>
              <ExternalLink size={18} color="#666" />
            </button>

            <button onClick={handleOpenWebsite} style={styles.socialButton}>
              <div style={{...styles.socialIcon, ...styles.websiteIcon}}>
                <Globe size={22} color="#fff" />
              </div>
              <div style={styles.socialInfo}>
                <span style={styles.socialName}>{t('officialWebsite')}</span>
                <span style={styles.socialHandle}>footparadiseart.com</span>
              </div>
              <ExternalLink size={18} color="#666" />
            </button>
          </div>

          {/* Support Section */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <Mail size={24} color="#FF1493" />
              <h3 style={styles.sectionTitle}>{t('support')}</h3>
            </div>
            <p style={styles.sectionText}>
              {t('supportText')}
            </p>
          </div>

          {/* Credits */}
          <div style={styles.creditsSection}>
            <h4 style={styles.creditsTitle}>{t('credits')}</h4>
            <div style={styles.creditsList}>
              <div style={styles.creditItem}>
                <span style={styles.creditRole}>{t('artStory')}</span>
                <span style={styles.creditName}>FootParadise Team</span>
              </div>
              <div style={styles.creditItem}>
                <span style={styles.creditRole}>{t('appDevelopment')}</span>
                <span style={styles.creditName}>FootParadise</span>
              </div>
            </div>
          </div>

          {/* Version */}
          <div style={styles.versionSection}>
            <p style={styles.versionText}>{t('version')} {CURRENT_APP_VERSION}</p>
            <p style={styles.copyright}>© 2024 FootParadise. {t('allRightsReserved')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundImage: 'url(/wallpaper.png)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    backgroundColor: '#1a1a2e',
    color: 'white',
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(255, 20, 147, 0.3)',
    ...fpHeader,
  },
  backButton: {
    background: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    cursor: 'pointer',
    padding: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backdropFilter: 'blur(10px)',
    transition: 'background-color 0.2s',
    ...fpBackButton,
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    margin: 0,
    textAlign: 'center',
    ...fpHeaderTitle,
  },
  spacer: {
    width: '44px',
  },
  content: {
    flex: 1,
    padding: '20px',
    overflowY: 'auto',
    display: 'flex',
    justifyContent: 'center',
  },
  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '20px',
    padding: '30px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 8px 32px rgba(255, 20, 147, 0.3)',
    ...fpCard,
  },
  brandSection: {
    textAlign: 'center',
    marginBottom: '30px',
    paddingBottom: '25px',
    borderBottom: '2px solid rgba(255, 20, 147, 0.2)',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoImage: {
    width: '180px',
    height: 'auto',
    objectFit: 'contain',
  },
  brandTagline: {
    fontSize: '14px',
    color: '#666',
    margin: '12px 0 0 0',
    fontStyle: 'italic',
  },
  section: {
    marginBottom: '25px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  sectionText: {
    fontSize: '14px',
    color: '#555',
    lineHeight: '1.6',
    margin: 0,
  },
  socialButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    padding: '14px 16px',
    backgroundColor: '#f8f8f8',
    border: '2px solid rgba(255, 20, 147, 0.2)',
    borderRadius: '12px',
    cursor: 'pointer',
    marginTop: '16px',
    transition: 'all 0.2s ease',
    ...fpStickerSmall,
  },
  socialIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    backgroundColor: '#000',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 'bold',
  },
  websiteIcon: {
    background: 'linear-gradient(135deg, #FF1493 0%, #FF69B4 100%)',
  },
  socialInfo: {
    flex: 1,
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
  },
  socialName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#333',
  },
  socialHandle: {
    fontSize: '13px',
    color: '#666',
  },
  creditsSection: {
    backgroundColor: 'rgba(255, 20, 147, 0.05)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
  },
  creditsTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#FF1493',
    margin: '0 0 16px 0',
    textAlign: 'center',
  },
  creditsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  creditItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  creditRole: {
    fontSize: '13px',
    color: '#666',
  },
  creditName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
  },
  versionSection: {
    textAlign: 'center',
    paddingTop: '15px',
    borderTop: '1px solid rgba(0, 0, 0, 0.1)',
  },
  versionText: {
    fontSize: '12px',
    color: '#999',
    margin: '0 0 4px 0',
  },
  copyright: {
    fontSize: '11px',
    color: '#bbb',
    margin: 0,
  },
};
